import { FoodsIcon, GenericAccordion, Typography } from "@foodpilot/foods";
import { Box, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useGetSomeIngredientReferencesUnified } from "src/api/client-api/ingredientReferenceUnified/useGetSomeIngredientReferencesUnified";
import { IngredientReferenceUnified, buildCompositeId } from "src/api/client-api/ingredientReferenceUnified/types";
import { ModifyButton } from "./ModifyButton";
import { SupplierFormType } from "../../SupplierForm.types";
import type { SupplierFormAppearance } from "../../SupplierForm.types";
import { FoodsLoadingIndicator } from "src/components/EntityForm/FoodsFields";

type IngredientSectionProps = {
    appearance?: SupplierFormAppearance;
    supplier: SupplierFormType;
};
export const IngredientSection = (props: IngredientSectionProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { appearance = "legacy", supplier } = props;

    const compositeIds = (supplier.ingredientSuppliers ?? [])
        .map((ingSup) =>
            ingSup.ingredient.ingredientReferenceId !== undefined ?
                buildCompositeId(ingSup.ingredient.referenceSource ?? "sure", ingSup.ingredient.ingredientReferenceId)
            :   undefined,
        )
        .filter((id): id is string => id !== undefined);

    const { ingredientReferences } = useGetSomeIngredientReferencesUnified(compositeIds);
    const resolvedIngredientReferences = compositeIds.length === 0 ? [] : ingredientReferences;

    const compositeIdToRef = resolvedIngredientReferences?.reduce(
        (acc, ref) => {
            acc[ref.compositeId] = ref;
            return acc;
        },
        {} as Record<string, IngredientReferenceUnified>,
    );

    const border = `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.strokes.containerDivider.color}`;

    return (
        <GenericAccordion
            headerIcon={<FoodsIcon icon="ingredients" size={3} />}
            headerText={t("menu.products.ingredients", "Ingredients")}
            controls={<ModifyButton appearance={appearance} pageNumber={2} />}
        >
            {resolvedIngredientReferences === undefined ?
                <Stack alignItems="center" sx={{ minHeight: theme.spacing(10) }} justifyContent="center">
                    <FoodsLoadingIndicator label={t("common.loading", "Loading")} />
                </Stack>
            :   <Box sx={{ overflowX: "auto" }}>
                    <Box
                        component="table"
                        aria-label={t("menu.products.ingredients", "Ingredients")}
                        sx={{
                            borderCollapse: "collapse",
                            width: "100%",
                            ...(appearance === "legacy" && {
                                "& th, & td": { minWidth: theme.spacing(22.5) },
                            }),
                        }}
                    >
                        <Box component="thead">
                            <Box component="tr">
                                {[t("nom"), t("reference"), t("filiere")].map((label) => (
                                    <Box
                                        component="th"
                                        key={label}
                                        sx={{
                                            borderBottom: border,
                                            color: theme.custom.grey[2000],
                                            minWidth: theme.spacing(22.5),
                                            padding: theme.spacing(2, 0),
                                        }}
                                    >
                                        <Typography variant={appearance === "legacy" ? "body" : "body-medium"}>
                                            {label}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {supplier.ingredientSuppliers?.map((ingredientSupplier, index, ingredients) => {
                                const ingredient = ingredientSupplier.ingredient;
                                const compositeId =
                                    ingredient.ingredientReferenceId !== undefined ?
                                        buildCompositeId(
                                            ingredient.referenceSource ?? "sure",
                                            ingredient.ingredientReferenceId,
                                        )
                                    :   undefined;
                                const refName =
                                    compositeId !== undefined ? compositeIdToRef?.[compositeId]?.name : undefined;

                                return (
                                    <Box component="tr" key={ingredient["@id"]}>
                                        <Box
                                            component="td"
                                            sx={{
                                                borderBottom: index === ingredients.length - 1 ? 0 : border,
                                                padding: theme.spacing(2, 0),
                                            }}
                                        >
                                            <Typography variant="body-regular">{ingredient.name}</Typography>
                                        </Box>
                                        <Box
                                            component="td"
                                            sx={{
                                                borderBottom: index === ingredients.length - 1 ? 0 : border,
                                                padding: theme.spacing(2, 0),
                                            }}
                                        >
                                            <Typography variant="body-regular">{refName ?? "-"}</Typography>
                                        </Box>
                                        <Box
                                            component="td"
                                            sx={{
                                                borderBottom: index === ingredients.length - 1 ? 0 : border,
                                                padding: theme.spacing(2, 0),
                                            }}
                                        >
                                            <Typography variant="body-regular">
                                                {ingredient.ingredientLine?.name ?? "-"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            }
        </GenericAccordion>
    );
};
